package ru.chm.gradereport.navigation

/**
 * Browser hash paths, e.g. `#/home`, `#/courses/{id}`, `#/public/{token}`, `#/hw/{homeworkToken}`.
 */
sealed interface AppPath {
    data object Login : AppPath
    data object Home : AppPath
    data object Reviewers : AppPath
    data class Course(val courseId: String) : AppPath
    data class Public(val token: String) : AppPath

    /** Homework-only page: its own token, unrelated to the public sheet token. */
    data class PublicHomework(val token: String) : AppPath
    data class PublicStudent(val token: String, val studentUid: String) : AppPath
    data class CheckIn(val token: String) : AppPath
}

fun AppPath.toHashPath(): String = when (this) {
    AppPath.Login -> "/login"
    AppPath.Home -> "/home"
    AppPath.Reviewers -> "/reviewers"
    is AppPath.Course -> "/courses/${courseId}"
    is AppPath.Public -> "/public/${token}"
    is AppPath.PublicHomework -> "/hw/${token}"
    is AppPath.PublicStudent -> "/public/${token}/student/${studentUid}"
    is AppPath.CheckIn -> "/checkin/${token}"
}

fun parseAppHashPath(raw: String): AppPath? {
    val parts = raw.removePrefix("#").trim('/').split('/').filter { it.isNotBlank() }
    if (parts.isEmpty()) return null
    return when (parts[0]) {
        "login" -> AppPath.Login
        "home" -> AppPath.Home
        "reviewers", "teachers" -> AppPath.Reviewers // "teachers" = legacy hash
        "courses" -> parts.getOrNull(1)?.takeIf { it.isNotBlank() }?.let { AppPath.Course(it) }
        "checkin" -> parts.getOrNull(1)?.takeIf { it.isNotBlank() }?.let { AppPath.CheckIn(it) }
        "hw", "homework" -> parts.getOrNull(1)?.takeIf { it.isNotBlank() }?.let { AppPath.PublicHomework(it) }
        "public" -> {
            val token = parts.getOrNull(1)?.takeIf { it.isNotBlank() } ?: return null
            when (parts.getOrNull(2)) {
                "student" -> {
                    val studentUid = parts.getOrNull(3)?.takeIf { it.isNotBlank() } ?: return null
                    AppPath.PublicStudent(token, studentUid)
                }
                else -> AppPath.Public(token)
            }
        }
        else -> null
    }
}
